// ============================================================================
// Cierre de sesión remoto y entre pestañas. Archivo IDÉNTICO en CC-CONSOLE y
// WAYAK: une el `forceRemoteLogout` de WAYAK (antes en tokenUtils, con el
// texto que manda el servidor) y el porte de CC-CONSOLE (Fase 0 de seguridad).
//
// Si la sesión se cierra en otro dispositivo (Perfil → Sesiones, el límite de
// sesiones, un administrador) o se cierra sesión en otra pestaña, esta pestaña
// sale en cuanto se entera, por cualquiera de estas vías:
//
//   1. Otra pestaña del mismo navegador cerró sesión: `clearSession` avisa por
//      BroadcastChannel("auth") —con el motivo— y, como respaldo, el evento
//      `storage` ve que el `token` compartido desapareció.
//   2. Push FCM `session_closed` (el backend lo manda al cerrar sesiones de un
//      usuario): si trae los ids cerrados y el nuestro está, se sale; si no
//      los trae, se le pregunta al servidor por NUESTRA sesión y el
//      interceptor de `utils/api.ts` hace el resto si ya no vale.
//   3. El interceptor reconoce `SESSION_CLOSED` / `SESSION_NOT_FOUND` (también
//      el 404 de sesión) y avisa.
//
// Qué hace cada fork con el push está en su `firebaseMessaging.ts` (si muestra
// el texto del servidor, si deja ver la notificación cuando se cerró OTRA
// sesión del usuario); aquí sólo las opciones explícitas de abajo.
// ============================================================================

import Swal from "sweetalert2";
import { DIALOG_COLORS } from "../components/ui/dialog";
import {
  AUTH_CHANNEL,
  SESSION_CLOSED_MESSAGE,
  clearSession,
  isLoggingOut,
  setLoggingOut,
  type SessionClosedReason,
} from "./tokenUtils";
import { useUserStore } from "../store/userStore";

type Lang = "es" | "en";

/** Por qué sale ESTA pestaña. */
export type RemoteLogoutReason = "remote" | "otherTab" | "expired";

const TEXTS: Record<Lang, Record<RemoteLogoutReason, { title: string; text: string }> & { button: string }> = {
  es: {
    remote: {
      title: "Sesión cerrada",
      text: "Tu sesión se cerró desde otro dispositivo o por un administrador.",
    },
    otherTab: {
      title: "Sesión cerrada",
      text: "Cerraste sesión en otra pestaña de este navegador.",
    },
    expired: {
      title: "Sesión expirada",
      text: "Tu sesión ha expirado o fue cerrada. Por favor, inicia sesión nuevamente.",
    },
    button: "Ir al Login",
  },
  en: {
    remote: {
      title: "Session closed",
      text: "Your session was closed from another device or by an administrator.",
    },
    otherTab: {
      title: "Session closed",
      text: "You signed out in another tab of this browser.",
    },
    expired: {
      title: "Session expired",
      text: "Your session has expired or was closed. Please log in again.",
    },
    button: "Go to Login",
  },
};

const langOf = (): Lang =>
  (localStorage.getItem("i18nextLng") || "es").split("-")[0] === "en" ? "en" : "es";

/** Motivo con el que ESTA pestaña avisa a las demás al limpiar. */
const BROADCAST_REASON: Record<RemoteLogoutReason, SessionClosedReason> = {
  remote: "remote",
  otherTab: "logout",
  expired: "expired",
};

/** ¿Esta pestaña tiene a alguien con sesión en memoria? (no la de otra pestaña) */
const hasSessionInThisTab = (): boolean => Boolean(useUserStore.getState().user);

/** Texto propio para el aviso (p. ej. el título y cuerpo que manda el servidor). */
export interface RemoteLogoutTexts {
  title?: string;
  message?: string;
}

const nonEmpty = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

/**
 * Sale de la sesión porque se cerró en otro lado: limpia sin llamar al backend
 * (la sesión ya no existe allá), avisa y lleva al login.
 *
 * @param texts título/mensaje propios; lo que falte sale del texto por motivo.
 */
export const forceRemoteLogout = async (
  reason: RemoteLogoutReason = "remote",
  texts?: RemoteLogoutTexts,
) => {
  if (isLoggingOut()) return;
  setLoggingOut(true);
  Swal.close();

  await clearSession(false, BROADCAST_REASON[reason]);

  const t = TEXTS[langOf()];
  await Swal.fire({
    icon: "warning",
    title: nonEmpty(texts?.title) ?? t[reason].title,
    text: nonEmpty(texts?.message) ?? t[reason].text,
    confirmButtonText: t.button,
    confirmButtonColor: DIALOG_COLORS.success,
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  window.location.href = "/";
};

/**
 * ¿La señal `session_closed` es para ESTA sesión?
 * `true` / `false` si trae los ids cerrados; `null` si no los trae.
 */
export const closedSessionMatches = (data: Record<string, unknown> | null | undefined): boolean | null => {
  if (!data) return null;
  const raw = data.closedSessionIds ?? data.sessionId;
  const ids = (Array.isArray(raw) ? raw : String(raw ?? "").split(","))
    .map((id) => String(id).trim())
    .filter(Boolean);
  if (ids.length === 0) return null;
  const mine = localStorage.getItem("sessionId");
  return Boolean(mine && ids.includes(mine));
};

export interface RemoteSessionSignalOptions {
  /**
   * `true`: el aviso usa el `title` / `body` que manda el servidor en el push
   * (como siempre hizo WAYAK). Sin esto, el texto fijo bilingüe por motivo.
   */
  serverTexts?: boolean;
}

/**
 * Atiende la señal `session_closed` (push FCM o mensaje del service worker).
 * Devuelve `true` si la consumió (no hace falta mostrar la notificación
 * genérica).
 *
 * @param verify pregunta al servidor por la sesión propia; si ya no vale, el
 *   interceptor de la API cierra la sesión y avisa (inyectable para pruebas).
 */
export const handleRemoteSessionClosedSignal = async (
  data: Record<string, unknown> | null | undefined,
  verify: () => Promise<unknown>,
  options: RemoteSessionSignalOptions = {},
): Promise<boolean> => {
  if (!hasSessionInThisTab() || isLoggingOut()) return true;
  const matches = closedSessionMatches(data);
  if (matches === true) {
    await forceRemoteLogout(
      "remote",
      options.serverTexts ? { title: nonEmpty(data?.title), message: nonEmpty(data?.body) } : undefined,
    );
    return true;
  }
  if (matches === false) return false; // se cerró OTRA sesión del usuario: sólo informar
  try {
    await verify();
  } catch {
    // El interceptor ya se encargó si era un fallo de sesión; si no, nada que hacer.
  }
  return false;
};

/** Motivo que manda la pestaña que cerró → texto en ésta. */
const reasonFromBroadcast = (reason: unknown): RemoteLogoutReason => {
  if (reason === "remote") return "remote";
  if (reason === "expired") return "expired";
  return "otherTab";
};

/**
 * Escucha el cierre de sesión en otras pestañas. Devuelve la función que deja
 * de escuchar. Se monta UNA vez (App.tsx).
 */
export const listenForCrossTabLogout = (): (() => void) => {
  const onSignal = (reason: RemoteLogoutReason) => {
    if (!hasSessionInThisTab() || isLoggingOut()) return;
    void forceRemoteLogout(reason);
  };

  let channel: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.onmessage = (event: MessageEvent) => {
        if (event.data?.type === SESSION_CLOSED_MESSAGE) onSignal(reasonFromBroadcast(event.data?.reason));
      };
    }
  } catch {
    channel = null;
  }

  // Respaldo (navegadores sin BroadcastChannel): el token es compartido entre
  // pestañas; si otra lo borró, esta sesión ya no tiene con qué hablar.
  const onStorage = (event: StorageEvent) => {
    if ((event.key === "token" || event.key === null) && !localStorage.getItem("token")) onSignal("otherTab");
  };
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener("storage", onStorage);
    try {
      channel?.close();
    } catch {
      /* nada */
    }
  };
};
